import React, { useEffect, useMemo, useState } from "react";
import { URL } from "../../utils/constants";
import { Link } from "react-router-dom";
import "./TrashView.css";
import { toast } from "../map/toast";
import OCKToasts from "./OCKToasts";

function buildApiUrl(pathOrUrl) {
  const value = String(pathOrUrl || "").trim();

  if (!value) return "";

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  const base = String(URL || "").replace(/\/+$/g, "");
  const path = value.startsWith("/") ? value : `/${value}`;

  return `${base}${path}`;
}

function formatDateTime(value) {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleString("ru-RU");
}

function formatSize(size) {
  if (size === null || size === undefined || Number.isNaN(Number(size))) return "—";

  const bytes = Number(size);

  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;

  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} ГБ`;
}

function getDaysLeft(expiresAt) {
  if (!expiresAt) return "—";

  const expires = new Date(expiresAt).getTime();
  if (Number.isNaN(expires)) return "—";

  const diff = expires - Date.now();

  if (diff <= 0) return "срок истёк";

  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (days === 1) return "1 день";
  if (days > 1 && days < 5) return `${days} дня`;

  return `${days} дней`;
}

function getDocName(doc) {
  return (
    doc?.name ||
    doc?.original_filename ||
    doc?.originalFilename ||
    "Без названия"
  );
}

function getObjectsText(doc) {
  const names = Array.isArray(doc?.object_names) ? doc.object_names : [];

  if (!names.length) return "—";

  return names.join(", ");
}

function normalizeMeGroups(user) {
  let groups = user?.groups || user?.group_names || [];

  if (Array.isArray(groups) && groups.length && typeof groups[0] === "object") {
    groups = groups.map((g) => g?.name);
  }

  return (groups || []).map((g) => String(g || "").toLowerCase().trim());
}

function canUseTrashPanel(user) {
  const groups = normalizeMeGroups(user);

  return (
    Boolean(user?.is_staff) ||
    Boolean(user?.is_superuser) ||
    groups.includes("point_ock")
  );
}

function canViewTrashPanel(user) {
  const groups = normalizeMeGroups(user);

  return (
    Boolean(user?.is_staff) ||
    Boolean(user?.is_superuser) ||
    Boolean(user?.documentation_access) ||
    groups.includes("point_ock")
  );
}

export default function TrashView({ user, tokenProp }) {
  const [me, setMe] = useState(user || null);
  const [docs, setDocs] = useState([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);

  const getToken = () => tokenProp || localStorage.getItem("auth_token");

  const canView = useMemo(() => canViewTrashPanel(me), [me]);
  const canManage = useMemo(() => canUseTrashPanel(me), [me]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 350);

    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let mounted = true;

    const applyFromObj = (obj = {}) => {
      if (mounted) setMe(obj);
    };

    try {
      const raw = localStorage.getItem("user");
      const parsed = JSON.parse(raw || "{}");

      if (parsed && (parsed.id || parsed.pk || parsed.username)) {
        applyFromObj(parsed);
      }
    } catch (e) {
      console.warn("Не удалось прочитать user из localStorage:", e);
    }

    const token = getToken();

    if (!token) {
      if (mounted) setLoading(false);
      return () => {
        mounted = false;
      };
    }

    const endpoints = [
      `${URL}/api/me/`,
      `${URL}/api/users/me/`,
      `${URL}/users/me/`,
      `${URL}/api/auth/users/me/`,
    ];

    (async () => {
      for (const ep of endpoints) {
        try {
          const res = await fetch(ep, {
            headers: {
              Authorization: `Token ${token}`,
            },
          });

          if (!res.ok) continue;

          const data = await res.json();
          applyFromObj(data);
          break;
        } catch (e) {
          console.warn("Ошибка получения профиля:", ep, e);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [tokenProp, user]);

  const loadTrashDocs = async () => {
    const token = getToken();

    if (!token) {
      setDocs([]);
      setLoading(false);
      toast.warn("Нет токена авторизации");
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("is_deleted", "true");

      if (debouncedQuery) {
        params.set("q", debouncedQuery);
      }

      const res = await fetch(
        buildApiUrl(`/api/ock-object-docs/?${params.toString()}`),
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      const data = await res.json().catch(() => []);

      if (!res.ok) {
        throw new Error(
          typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`
        );
      }

      const items = Array.isArray(data) ? data : data.items || data.results || [];

      setDocs(items);
      setSelectedIds(new Set());
    } catch (e) {
      console.error("Failed to load trash docs:", e);
      setDocs([]);
      toast.error("Ошибка загрузки корзины");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      setDocs([]);
      return;
    }

    loadTrashDocs();
  }, [canView, debouncedQuery]);

  const selectedDocs = useMemo(
    () => docs.filter((doc) => selectedIds.has(String(doc.id))),
    [docs, selectedIds]
  );

  const allVisibleSelected =
    docs.length > 0 && docs.every((doc) => selectedIds.has(String(doc.id)));

  const clearSearch = () => setQuery("");

  const toggleSelect = (docId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const id = String(docId);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allVisibleSelected) {
        docs.forEach((doc) => next.delete(String(doc.id)));
      } else {
        docs.forEach((doc) => next.add(String(doc.id)));
      }

      return next;
    });
  };

  const restoreDoc = async (doc) => {
    if (!canManage) return;

    const ok = await toast.confirm(`Восстановить файл "${getDocName(doc)}"?`);
    if (!ok) return;

    const token = getToken();

    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    setActionLoadingId(doc.id);

    try {
      const res = await fetch(
        buildApiUrl(`/api/ock-object-docs/${doc.id}/restore/`),
        {
          method: "POST",
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`
        );
      }

      setDocs((prev) => prev.filter((item) => String(item.id) !== String(doc.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(String(doc.id));
        return next;
      });

      toast.success("Файл восстановлен");
    } catch (e) {
      console.error("Restore failed:", e);
      toast.error("Ошибка восстановления файла");
    } finally {
      setActionLoadingId(null);
    }
  };

  const purgeDoc = async (doc) => {
    if (!canManage) return;

    const ok = await toast.confirm(
      `Удалить файл "${getDocName(doc)}" навсегда? Это действие нельзя отменить.`
    );

    if (!ok) return;

    const token = getToken();

    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    setActionLoadingId(doc.id);

    try {
      const res = await fetch(
        buildApiUrl(`/api/ock-object-docs/${doc.id}/purge/`),
        {
          method: "DELETE",
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`
        );
      }

      setDocs((prev) => prev.filter((item) => String(item.id) !== String(doc.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(String(doc.id));
        return next;
      });

      toast.success("Файл удалён навсегда");
    } catch (e) {
      console.error("Purge failed:", e);
      toast.error("Ошибка окончательного удаления");
    } finally {
      setActionLoadingId(null);
    }
  };

  const restoreSelected = async () => {
    if (!canManage) return;

    if (!selectedDocs.length) {
      toast.warn("Выберите хотя бы один файл");
      return;
    }

    const ok = await toast.confirm(
      `Восстановить выбранные файлы (${selectedDocs.length})?`
    );

    if (!ok) return;

    const token = getToken();

    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    const toastId = toast.loading(`Восстановление файлов: ${selectedDocs.length}`);

    try {
      const restoredIds = [];

      for (const doc of selectedDocs) {
        setActionLoadingId(doc.id);

        const res = await fetch(
          buildApiUrl(`/api/ock-object-docs/${doc.id}/restore/`),
          {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`
          );
        }

        restoredIds.push(String(doc.id));
      }

      const restoredSet = new Set(restoredIds);

      setDocs((prev) => prev.filter((doc) => !restoredSet.has(String(doc.id))));
      setSelectedIds(new Set());

      toast.update(toastId, `Восстановлено файлов: ${restoredIds.length}`, "success");
    } catch (e) {
      console.error("Bulk restore failed:", e);
      toast.update(toastId, "Ошибка пакетного восстановления", "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const purgeSelected = async () => {
    if (!canManage) return;

    if (!selectedDocs.length) {
      toast.warn("Выберите хотя бы один файл");
      return;
    }

    const ok = await toast.confirm(
      `Удалить выбранные файлы навсегда (${selectedDocs.length})? Это действие нельзя отменить.`
    );

    if (!ok) return;

    const token = getToken();

    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    const toastId = toast.loading(`Удаление файлов: ${selectedDocs.length}`);

    try {
      const purgedIds = [];

      for (const doc of selectedDocs) {
        setActionLoadingId(doc.id);

        const res = await fetch(
          buildApiUrl(`/api/ock-object-docs/${doc.id}/purge/`),
          {
            method: "DELETE",
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`
          );
        }

        purgedIds.push(String(doc.id));
      }

      const purgedSet = new Set(purgedIds);

      setDocs((prev) => prev.filter((doc) => !purgedSet.has(String(doc.id))));
      setSelectedIds(new Set());

      toast.update(toastId, `Удалено навсегда: ${purgedIds.length}`, "success");
    } catch (e) {
      console.error("Bulk purge failed:", e);
      toast.update(toastId, "Ошибка пакетного удаления", "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const openContextMenu = (e, doc) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      doc,
    });
  };

  const closeContextMenu = () => setContextMenu(null);

  useEffect(() => {
    if (!contextMenu) return;

    const close = () => closeContextMenu();
    const esc = (e) => {
      if (e.key === "Escape") closeContextMenu();
    };

    window.addEventListener("click", close);
    window.addEventListener("contextmenu", close);
    window.addEventListener("keydown", esc);

    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("keydown", esc);
    };
  }, [contextMenu]);

  if (!canView) {
    return <div>Доступно только пользователям группы OCK / archive</div>;
  }

  if (loading) {
    return <div>Загрузка корзины документов...</div>;
  }

  return (
    <div className="ock-cloud ock-trash">
      <div className="ock-cloud__headerCard">
        <div className="ock-cloud__bar">
          <div className="ock-trash__titleBlock">
            <div className="ock-trash__title">Корзина облака ОСК</div>
            <div className="ock-trash__subtitle">
              Документы хранятся в корзине 7 дней. После очистки восстановление через интерфейс невозможно.
            </div>
          </div>

          <button
            className="ock-cloud__barBtn ock-trash__refreshBtn"
            type="button"
            onClick={loadTrashDocs}
            disabled={loading}
            title="Обновить список"
          >
            ↻
          </button>
        </div>

        <div className="ock-cloud__toolbar">
          <div className="ock-cloud__searchWrap">
            <div className="ock-cloud__searchBar">
              <input
                className="ock-cloud__search"
                placeholder="Поиск по имени, пути или объекту…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />

              <button
                className="ock-cloud__clearBtn"
                onClick={clearSearch}
                title="Очистить"
                type="button"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="ock-cloud__toolbarActions">
            <button
              className="ock-cloud__btn ock-cloud__btn--primary"
              type="button"
              onClick={restoreSelected}
              disabled={!canManage || selectedDocs.length === 0}
            >
              Восстановить выбранные
            </button>

            <button
              className="ock-cloud__btn ock-cloud__btn--danger"
              type="button"
              onClick={purgeSelected}
              disabled={!canManage || selectedDocs.length === 0}
            >
              Удалить навсегда
            </button>

            <button className="ock-cloud__btn ock-cloud__btn--secondary" type="button">
              <Link to="/cloud" className="ock-link">
                Облако
              </Link>
            </button>
          </div>
        </div>

        <div className="ock-cloud__submeta">
          <span className="ock-cloud__metaChip ock-cloud__metaChip--path">
            <span className="ock-cloud__metaLabel">Раздел</span>
            <b>Корзина</b>
          </span>

          <span className="ock-cloud__metaChip">
            <span className="ock-cloud__metaLabel">Файлов</span>
            <b>{docs.length}</b>
          </span>

          <span className="ock-cloud__metaChip">
            <span className="ock-cloud__metaLabel">Выбрано</span>
            <b>{selectedDocs.length}</b>
          </span>

          <span className="ock-cloud__metaChip ock-trash__metaDanger">
            <span className="ock-cloud__metaLabel">Режим</span>
            <b>{canManage ? "управление" : "только просмотр"}</b>
          </span>
        </div>
      </div>

      <div className="ock-trash__layout">
        <section className="ock-cloud__content ock-trash__content">
          {docs.length === 0 ? (
            <div className="ock-cloud__empty">Корзина пуста</div>
          ) : (
            <div className="ock-cloud__scroll">
              <div className="ock-cloud__listHead">
                <label className="ock-cloud__selectAll">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                  />
                  <span>Выбрать всё</span>
                </label>
              </div>

              <div className="ock-cloud__list">
                {docs.map((doc) => {
                  const id = String(doc.id);
                  const isSelected = selectedIds.has(id);
                  const isActionLoading = String(actionLoadingId) === id;
                  const docName = getDocName(doc);

                  return (
                    <div
                      key={doc.id}
                      className="ock-cloud__item ock-cloud__item--file ock-trash__item"
                      onContextMenu={(e) => openContextMenu(e, doc)}
                    >
                      <div className="ock-cloud__checkboxCol">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(doc.id)}
                        />
                      </div>

                      <button
                        className="ock-cloud__fileIcon ock-trash__fileIcon"
                        type="button"
                        title="Файл в корзине"
                        disabled
                      >
                        🗑️
                      </button>

                      <div className="ock-cloud__fileMain">
                        <button
                          type="button"
                          className="ock-cloud__fileName"
                          title={docName}
                          onClick={() => toggleSelect(doc.id)}
                        >
                          {docName}
                        </button>

                        <div className="ock-cloud__fileInfo">
                          <span>Размер: {formatSize(doc.size)}</span>
                          <span>Тип: {doc.file_ext || "—"}</span>
                          <span>Удалил: {doc.deleted_by_username || "—"}</span>
                          <span>Удалено: {formatDateTime(doc.deleted_at)}</span>
                          <span>Истекает: {formatDateTime(doc.expires_at)}</span>
                          <span>Осталось: {getDaysLeft(doc.expires_at)}</span>
                        </div>

                        <div className="ock-trash__objects" title={getObjectsText(doc)}>
                          Объекты: {getObjectsText(doc)}
                        </div>

                        <div
                          className="ock-cloud__filePath"
                          title={doc.original_cloud_path || doc.trash_path || ""}
                        >
                          Исходный путь: {doc.original_cloud_path || "—"}
                        </div>

                        {doc.trash_path ? (
                          <div className="ock-cloud__filePath" title={doc.trash_path}>
                            Путь в корзине: {doc.trash_path}
                          </div>
                        ) : null}
                      </div>

                      <div className="ock-cloud__iconActions ock-trash__iconActions">
                        <button
                          className="ock-cloud__iconBtn ock-trash__iconBtnRestore"
                          type="button"
                          onClick={() => restoreDoc(doc)}
                          disabled={!canManage || isActionLoading}
                          title="Восстановить"
                        >
                          ♻️
                        </button>

                        <button
                          className="ock-cloud__iconBtn ock-cloud__iconBtn--danger"
                          type="button"
                          onClick={() => purgeDoc(doc)}
                          disabled={!canManage || isActionLoading}
                          title="Удалить навсегда"
                        >
                          ❌
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>

      {contextMenu && (
        <>
          <div
            className="ock-cloud__contextBackdrop"
            onClick={closeContextMenu}
            onContextMenu={(e) => {
              e.preventDefault();
              closeContextMenu();
            }}
          />

          <div
            className="ock-cloud__radialMenu ock-trash__radialMenu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <button
              className="ock-cloud__radialItem ock-trash__radialRestore"
              type="button"
              disabled={!canManage}
              onClick={() => {
                const doc = contextMenu.doc;
                closeContextMenu();
                restoreDoc(doc);
              }}
            >
              ♻️
              <span>Вернуть</span>
            </button>

            <button
              className="ock-cloud__radialItem ock-trash__radialPurge"
              type="button"
              disabled={!canManage}
              onClick={() => {
                const doc = contextMenu.doc;
                closeContextMenu();
                purgeDoc(doc);
              }}
            >
              ❌
              <span>Навсегда</span>
            </button>

            <div className="ock-cloud__radialCenter">🗑️</div>
          </div>
        </>
      )}

      <OCKToasts />
    </div>
  );
}